import { useEffect, useState } from 'react';
import { decryptSensitiveFields, isEncrypted } from '@/lib/encryption';

interface DecryptedFields {
  immatriculation?: string | null;
  mmsi?: string | null;
  numero_hin?: string | null;
}

const EMPTY_RESULT: DecryptedFields = {
  immatriculation: null,
  mmsi: null,
  numero_hin: null,
};

/**
 * Sanitize: never let "enc:..." raw ciphertext leak to the UI.
 */
const sanitize = (fields: DecryptedFields): DecryptedFields => ({
  immatriculation: isEncrypted(fields.immatriculation) ? null : fields.immatriculation,
  mmsi: isEncrypted(fields.mmsi) ? null : fields.mmsi,
  numero_hin: isEncrypted(fields.numero_hin) ? null : fields.numero_hin,
});

/**
 * Déchiffre à la volée les champs sensibles (immatriculation, MMSI, HIN) d'un bateau.
 * Si les valeurs ne sont pas chiffrées (préfixe `enc:`), elles sont renvoyées telles quelles.
 * Si le déchiffrement échoue, les valeurs chiffrées sont remplacées par null
 * pour ne JAMAIS exposer le ciphertext brut à l'utilisateur.
 */
export const useDecryptedBoatFields = (
  boatId: string | undefined,
  fields: DecryptedFields
): { decrypted: DecryptedFields; loading: boolean } => {
  const [decrypted, setDecrypted] = useState<DecryptedFields>(() => EMPTY_RESULT);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const needsDecrypt =
      isEncrypted(fields.immatriculation) ||
      isEncrypted(fields.mmsi) ||
      isEncrypted(fields.numero_hin);

    if (!boatId || !needsDecrypt) {
      setDecrypted(sanitize(fields));
      setLoading(false);
      return;
    }

    let cancelled = false;
    setDecrypted(EMPTY_RESULT);
    setLoading(true);
    decryptSensitiveFields(boatId)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          console.error('[useDecryptedBoatFields] decrypt error:', error);
          setDecrypted(EMPTY_RESULT);
        } else {
          setDecrypted(sanitize({
            immatriculation: data.immatriculation ?? fields.immatriculation,
            mmsi: data.mmsi ?? fields.mmsi,
            numero_hin: data.numero_hin ?? fields.numero_hin,
          }));
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useDecryptedBoatFields] exception:', err);
        setDecrypted(EMPTY_RESULT);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [boatId, fields.immatriculation, fields.mmsi, fields.numero_hin]);

  return { decrypted, loading };
};
